import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
// import { useAutoFetch } from '../contexts/AutoFetchContext'; // Disabled: server-side implementation planned
import { getUserSearches, deleteSearch, markListingsAsRead, updateSearchAutoFetch, updateSearchSettings } from '../lib/firebaseService';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Search,
  Calendar,
  ExternalLink,
  Trash2,
  RefreshCw,
  Eye,
  Clock,
  Car,
  Zap
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const SavedSearches = ({ onSearchSelect, onRefreshSearch }) => {
  const { user } = useAuth();
  // const { updateSearchAutoFetch: updateAutoFetchContext } = useAutoFetch(); // Disabled: server-side implementation planned
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingSearchId, setRefreshingSearchId] = useState(null);

  useEffect(() => {
    if (user) {
      loadUserSearches();
    }
  }, [user]);

  const loadUserSearches = async () => {
    try {
      setLoading(true);
      const result = await getUserSearches(user.uid);
      if (result.success) {
        setSearches(result.searches);
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger vos recherches sauvegardées.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error loading searches:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement des recherches.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSearch = async (searchId, searchName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la recherche "${searchName}" ?`)) {
      return;
    }

    try {
      const result = await deleteSearch(searchId);
      if (result.success) {
        setSearches(searches.filter(search => search.id !== searchId));
        toast({
          title: "Succès",
          description: "Recherche supprimée avec succès.",
        });
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la recherche.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error deleting search:', error);
      toast({
        title: "Erreur",
        description: "Erreur lors de la suppression.",
        variant: "destructive",
      });
    }
  };

  const handleRefreshSearch = async (searchId) => {
    try {
      setRefreshingSearchId(searchId);
      if (onRefreshSearch) {
        await onRefreshSearch(searchId);
        await loadUserSearches(); // Reload to get updated counts
      }
    } catch (error) {
      console.error('Error refreshing search:', error);
    } finally {
      setRefreshingSearchId(null);
    }
  };

  const handleMarkAsRead = async (searchId) => {
    try {
      const result = await markListingsAsRead(searchId);
      if (result.success) {
        // Update the local state to reflect the change
        setSearches(searches.map(search =>
          search.id === searchId
            ? { ...search, newListingCount: 0 }
            : search
        ));
        toast({
          title: "Succès",
          description: "Annonces marquées comme vues.",
        });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleAutoFetchToggle = async (searchId, currentAutoFetch, fetchInterval) => {
    try {
      const newAutoFetch = !currentAutoFetch;

      // Use backend API to update settings (includes scheduler integration)
      const result = await updateSearchSettings(searchId, {
        auto_refresh: newAutoFetch,
        refresh_interval: fetchInterval || 60
      });

      if (result.success) {
        // Also update Firebase directly for UI consistency (backend should have done this too)
        const firebaseResult = await updateSearchAutoFetch(searchId, newAutoFetch, fetchInterval || 60);

        if (firebaseResult.success) {
          // Update the local state
          setSearches(searches.map(search =>
            search.id === searchId
              ? { ...search, autoFetch: newAutoFetch, fetchInterval: fetchInterval || 60 }
              : search
          ));

          toast({
            title: newAutoFetch ? "Actualisation automatique activée" : "Actualisation automatique désactivée",
            description: newAutoFetch
              ? `Cette recherche sera actualisée automatiquement toutes les ${fetchInterval || 60} minutes.`
              : "L'actualisation automatique a été désactivée pour cette recherche.",
          });
        }
      } else {
        throw new Error(result.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error toggling auto-fetch:', error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'actualisation automatique.",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Jamais';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Jamais';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Jamais';
    }
  };

  const extractSearchInfo = (url) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);

      return {
        location: urlObj.pathname.includes('casablanca') ? 'Casablanca' : 'Maroc',
        brand: params.get('brand') || 'Toutes marques',
        model: params.get('model') || 'Tous modèles'
      };
    } catch {
      return {
        location: 'Maroc',
        brand: 'Toutes marques',
        model: 'Tous modèles'
      };
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <div className="text-center py-12">
        <Search className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Aucune recherche sauvegardée
        </h3>
        <p className="text-gray-600 mb-4">
          Commencez par créer votre première recherche pour suivre les annonces qui vous intéressent.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {searches.map((search) => {
        const searchInfo = extractSearchInfo(search.url);

        return (
          <Card
            key={search.id}
            className="group hover:shadow-lg transition-all duration-200 cursor-pointer border-l-4 border-l-blue-500"
            onClick={() => onSearchSelect && onSearchSelect(search)}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {search.name}
                  </CardTitle>
                  <div className="flex items-center text-sm text-gray-500 mt-1">
                    <Car className="w-4 h-4 mr-1" />
                    {searchInfo.brand} {searchInfo.model !== 'Tous modèles' && `• ${searchInfo.model}`}
                  </div>
                </div>
                {search.newListingCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {search.newListingCount} nouveau{search.newListingCount > 1 ? 'x' : ''}
                  </Badge>
                )}
              </div>
            </CardHeader>

            <CardContent className="pt-0">
              <div className="space-y-3">
                {/* Search Stats */}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    {search.listingCount || 0} annonce{(search.listingCount || 0) > 1 ? 's' : ''}
                  </span>
                  <span className="text-gray-500">
                    📍 {searchInfo.location}
                  </span>
                </div>

                {/* Last Scraped */}
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="w-4 h-4 mr-1" />
                  Dernière vérification: {formatDate(search.lastScraped)}
                </div>

                {/* Auto-fetch Toggle */}
                <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-100">
                  <div className="flex items-center space-x-2">
                    <Zap className={`w-4 h-4 ${search.autoFetch ? 'text-green-600' : 'text-gray-400'}`} />
                    <span className="text-gray-600">
                      Actualisation automatique
                      {search.autoFetch && (
                        <span className="text-green-600 ml-1">
                          ({search.fetchInterval || 60}min)
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAutoFetchToggle(search.id, search.autoFetch, search.fetchInterval);
                    }}
                    className={`
                      relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${search.autoFetch ? 'bg-green-600' : 'bg-gray-200'}
                    `}
                  >
                    <span
                      className={`
                        inline-block h-3 w-3 transform rounded-full bg-white transition-transform
                        ${search.autoFetch ? 'translate-x-5' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRefreshSearch(search.id);
                    }}
                    disabled={refreshingSearchId === search.id}
                    className="flex-1"
                  >
                    <RefreshCw className={`w-4 h-4 mr-1 ${refreshingSearchId === search.id ? 'animate-spin' : ''}`} />
                    {refreshingSearchId === search.id ? 'Actualisation...' : 'Actualiser'}
                  </Button>

                  {search.newListingCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(search.id);
                      }}
                      title="Marquer comme vu"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(search.url, '_blank');
                    }}
                    title="Voir sur Avito"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteSearch(search.id, search.name);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default SavedSearches;